import { useState } from "react";
import { useApi } from "@/lib/api/useApi";
import { useMutate } from "@/lib/api/useMutate";
import { queryCache } from "@/lib/api/queryCache";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { removeFromCart, updateCartItem } from "@/lib/api/cart";
import { cartQuery } from "@/lib/api/queries";
import { EmptyState } from "@/components/store/EmptyState";
import { Icon } from "@/components/store/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { PageTransition } from "@/components/store/PageTransition";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { imgUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { AuthModal } from "@/components/store/AuthModal";

function CartSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter animate-pulse">
      <div className="lg:col-span-8 min-w-0">
        <ul className="flex flex-col divide-y divide-outline-variant/25">
          {Array.from({ length: 2 }).map((_, index) => (
            <li key={index} className="flex gap-4 sm:gap-6 py-6 first:pt-0">
              <div className="w-24 sm:w-28 aspect-square shrink-0 rounded-xl bg-surface-container" />
              <div className="min-w-0 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="h-4 w-3/4 rounded-full bg-surface-container mb-2" />
                    <div className="h-2.5 w-16 rounded-full bg-surface-container mb-2" />
                    <div className="h-3 w-1/3 rounded-full bg-surface-container" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-surface-container" />
                </div>
                <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                  <div className="h-8 w-24 rounded-full bg-surface-container" />
                  <div className="h-4 w-16 rounded-full bg-surface-container" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-4">
        <div className="bg-surface-container-lowest p-6 sm:p-7 rounded-2xl border border-outline-variant/25">
          <div className="h-5 w-1/3 rounded-full bg-surface-container mb-5" />
          <div className="space-y-4 pb-5 border-b border-outline-variant/25">
            <div className="flex justify-between">
              <div className="h-3.5 w-16 rounded-full bg-surface-container" />
              <div className="h-3.5 w-12 rounded-full bg-surface-container" />
            </div>
            <div className="flex justify-between">
              <div className="h-3.5 w-24 rounded-full bg-surface-container" />
              <div className="h-3.5 w-16 rounded-full bg-surface-container" />
            </div>
          </div>
          <div className="pt-4 pb-6 flex justify-between">
            <div className="h-4 w-12 rounded-full bg-surface-container" />
            <div className="h-4 w-20 rounded-full bg-surface-container" />
          </div>
          <div className="h-12 w-full rounded-full bg-surface-container" />
        </div>
      </div>
    </div>
  );
}

function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { formatPrice, formatNumber } = useFormatters();
  const { productName, colorName, sizeName } = useLocalizedContent();
  const q = cartQuery();
  const { data: cart, loading } = useApi(q.queryKey, q.queryFn);

  const invalidate = () => queryCache.invalidate(cartQuery().queryKey);

  const quantityMutation = useMutate(
    ({ id, quantity }: { id: string; quantity: number }) =>
      updateCartItem(id, quantity),
    {
      onSuccess: invalidate,
      onError: (error: Error) => toast.error(error.message),
    }
  );

  const removeMutation = useMutate(
    (id: string) => removeFromCart(id),
    {
      onSuccess: () => {
        invalidate();
        toast.success(t("cart.removed"));
      },
      onError: (error: Error) => toast.error(error.message),
    }
  );

  const itemCount = (cart?.items ?? []).reduce((sum, item) => sum + item.quantity, 0);

  const stepper = (id: string, quantity: number, name: string) => (
    <div className="inline-flex items-center rounded-full border border-outline-variant/50 bg-surface-container-lowest">
      <button
        type="button"
        aria-label={t("common.decreaseQty", { name })}
        onClick={() => quantityMutation.mutate({ id, quantity: quantity - 1 })}
        className="w-8 h-8 flex items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-primary"
      >
        <Icon name="remove" className="text-[16px]" />
      </button>
      <span className="w-7 text-center text-[13px] font-label-sm text-primary">
        {formatNumber(quantity)}
      </span>
      <button
        type="button"
        aria-label={t("common.increaseQty", { name })}
        onClick={() => quantityMutation.mutate({ id, quantity: quantity + 1 })}
        className="w-8 h-8 flex items-center justify-center rounded-full text-on-surface-variant transition-colors hover:text-primary"
      >
        <Icon name="add" className="text-[16px]" />
      </button>
    </div>
  );

  return (
    <StoreLayout>
      <PageTransition>
        <main className="flex-grow pt-10 pb-section-gap-desktop px-grid-margin">
          <div className="max-w-7xl mx-auto">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 cursor-pointer hover:bg-surface-container-low transition-colors">
              <ArrowLeft className="w-4 h-4 me-2" />{t('product.back', { defaultValue: 'Back' })}
            </Button>
            <Reveal as="header" className="mb-10">
              <h1 className="text-headline-lg font-headline-lg text-primary">
                {t("cart.title")}
              </h1>
            </Reveal>

            {loading ? (
              <CartSkeleton />
            ) : (cart?.items ?? []).length === 0 ? (
              <div className="flex flex-col items-center">
                <EmptyState
                  variant="cart"
                  title={t("cart.empty")}
                  description={t("cart.emptyDesc")}
                />
              </div>
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
              <div className="lg:col-span-8 min-w-0">
                  <ul className="flex flex-col divide-y divide-outline-variant/25">
                    {(cart?.items ?? []).map((item) => (
                      <li key={item.id} className="flex gap-4 sm:gap-6 py-6 first:pt-0">
                        <Link
                          to={`/product/${item.slug}`}
                          className="w-24 sm:w-28 aspect-square shrink-0 overflow-hidden rounded-xl bg-surface-container"
                        >
                          <img
                            src={imgUrl(item.image)}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </Link>

                        <div className="min-w-0 flex-1 flex flex-col">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-body-lg font-headline-md text-primary truncate">
                                <Link to={`/product/${item.slug}`}>
                                  {productName(item.name)}
                                </Link>
                              </h3>
                              <p className="text-[11px] font-label-sm uppercase tracking-[0.14em] text-on-surface-variant/60 mt-1">
                                {t("cart.ref", { ref: item.ref })}
                              </p>
                              <p className="text-[12px] font-label-sm text-on-surface-variant mt-1.5">
                                {t("cart.sizeLine", {
                                  color: colorName(item.color),
                                  size: sizeName(item.size),
                                })}
                              </p>
                            </div>
                            <button
                              type="button"
                              aria-label={t("common.removeItem", {
                                name: productName(item.name),
                              })}
                              onClick={() => removeMutation.mutate(item.id)}
                              className="shrink-0 w-8 h-8 grid place-items-center rounded-full text-on-surface-variant/60 transition-colors hover:text-error"
                            >
                              <Icon name="delete" className="text-[18px]" />
                            </button>
                          </div>

                          <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                            {stepper(item.id, item.quantity, productName(item.name))}
                            <span className="text-body-md font-label-md text-primary">
                              {formatPrice(item.price * item.quantity)}
                            </span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>


                <Link
                  to="/shop"
                  className="mt-8 inline-flex items-center gap-2 group cursor-pointer w-fit"
                >
                  <Icon
                    name="arrow_back"
                    className="text-secondary text-[18px] transition-transform group-hover:-translate-x-1 rtl:rotate-180"
                  />
                  <span className="text-[12px] font-label-sm text-primary uppercase tracking-[0.16em]">
                    {t("common.continueShopping")}
                  </span>
                </Link>
              </div>

              {/* Summary */}
              <div className="lg:col-span-4">

                <div className="bg-surface-container-lowest p-6 sm:p-7 rounded-2xl luxury-shadow border border-outline-variant/25 sticky top-32">
                  <h2 className="text-body-lg font-headline-md text-primary mb-5">
                    {t("cart.summary")}
                  </h2>

                  <dl className="space-y-3 pb-5 border-b border-outline-variant/25">
                    <div className="flex items-baseline justify-between gap-4 text-[13px]">
                      <dt className="text-on-surface-variant">{t("common.subtotal")}</dt>
                      <dd className="font-label-sm text-primary">{formatPrice(cart?.subtotal ?? 0)}</dd>
                    </div>
                    <div className="flex items-baseline justify-between gap-4 text-[13px]">
                      <dt className="text-on-surface-variant">{t("cart.standardShipping")}</dt>
                      <dd className="font-label-sm text-secondary">
                        {t("common.complimentary")}
                      </dd>
                    </div>
                    {(cart?.discount ?? 0) > 0 && (
                      <div className="flex items-baseline justify-between gap-4 text-[13px]">
                        <dt className="text-on-surface-variant">
                          {t("cart.discount", { code: cart?.promoCode })}
                        </dt>
                        <dd className="font-label-sm text-secondary">
                          −{formatPrice(cart?.discount ?? 0)}
                        </dd>
                      </div>
                    )}
                    <div className="flex items-baseline justify-between gap-4 text-[13px]">
                      <dt className="text-on-surface-variant">{t("cart.estimatedTax")}</dt>
                      <dd className="font-label-sm text-primary">{formatPrice(cart?.tax ?? 0)}</dd>
                    </div>
                  </dl>

                  <div className="py-5 flex items-baseline justify-between gap-4">
                    <span className="text-[12px] font-label-sm uppercase tracking-[0.18em] text-on-surface-variant">
                      {t("common.total")}
                    </span>
                    <span className="text-headline-md font-headline-md text-primary whitespace-nowrap">
                      {formatPrice(cart?.total ?? 0)}
                    </span>
                  </div>
                  <p className="-mt-3 mb-6 text-end text-[10px] uppercase tracking-[0.16em] text-on-surface-variant/60">
                    {t("cart.duties")}
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        toast.error(t("auth.pleaseLogin", { defaultValue: "Please login first" }));
                        setShowAuthModal(true);
                      } else {
                        navigate("/checkout");
                      }
                    }}
                    className="w-full rounded-full bg-primary py-4 text-[12px] font-label-sm uppercase tracking-[0.18em] text-on-primary transition-all hover:bg-primary-container flex items-center justify-center gap-3 luxury-shadow cursor-pointer"
                  >
                    {t("cart.checkout")}
                    <Icon name="arrow_forward" className="text-[18px] rtl:rotate-180" />
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </main>
        <AuthModal
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setShowAuthModal(false);
            navigate("/checkout");
          }}
        />
      </PageTransition>
    </StoreLayout>
  );
}

export default CartPage;
